<script setup lang="ts">
  import { computed } from 'vue'

  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'

  import useShopifyClient from '@/composables/useShopifyClient'
  import useIcons from '@/composables/useIcons'
  import useI18n from '@/composables/useI18n'
  import useCourse from '@/composables/useCourse'
  import useBreakpoint from '@/composables/useBreakpoint'

  import { UserModule } from '@/store/modules/user'

  const { course, trialTimeLeft, isTrialExpired } = useCourse()
  const { createShopifyCheckout } = useShopifyClient()
  const { arrowRightIndicator } = useIcons()
  const { translateString } = useI18n()
  const { isDesktopDevice } = useBreakpoint()

  const daysLeftText = computed(() => {
    const { days, hours } = trialTimeLeft.value(course.value)

    if (days <= 0 && hours <= 0) {
      return translateString('courses.courseTrials.testExpired')
    }

    if (days >= 1) {
      return translateString('courses.courseTrials.onlyXDaysLeft', { days })
    }

    return translateString('courses.courseTrials.onlyXHoursLeft', { hours })
  })

  const buyNow = () => {
    createShopifyCheckout(UserModule.email || undefined, course.value?.shopifyProductVariantId)
  }

  const extendTrial = () => {
    // TODO: Implement extend trial
  }
</script>

<template lang="pug">
.trial-status-banner-wrapper(:class='{ "is-trial-expired": isTrialExpired(course) }')
  .trial-status-banner.header-spacing
    h5.trial-time-left {{ daysLeftText }}
    .trial-cta-wrapper
      KetchUpButton.extend-trial(@click.native='extendTrial', v-if='isDesktopDevice && false')
        h4.ketch-font-bold {{ $t('courses.courseTrials.extendTrial') }}
      KetchUpButton.buy-now(@click.native='buyNow', :class='{ "is-trial-expired": isTrialExpired(course) }')
        h4.ketch-font-bold {{ $t('selectCourse') }}
        SVGRenderer(
          :icon='arrowRightIndicator',
          :fill-color='"var(--primary-text-color)"',
          :has-hover='false',
          width='15px',
          height='10px'
        )
</template>

<style lang="postcss" scoped>
  .trial-status-banner-wrapper {
    @apply ketch-flex ketch-items-center ketch-justify-center ketch-bg-primary-color;

    .trial-status-banner {
      @apply ketch-py-c10 ketch-flex ketch-items-center ketch-justify-between;

      .trial-time-left {
        @apply ketch-text-white ketch-font-bold;
        filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.15));
      }

      .trial-cta-wrapper {
        @apply ketch-flex ketch-items-center ketch-space-x-c10;

        button {
          @apply ketch-w-auto ketch-h-auto ketch-py-c2 ketch-px-c10 ketch-space-x-c2 ketch-rounded-normal;
          @apply md:ketch-px-c10 md:ketch-space-x-c5;
        }

        button.extend-trial {
          @apply ketch-bg-white ketch-border-white;
          @apply md:hover:ketch-border-primary-text-color;
        }

        button.buy-now {
          @apply ketch-bg-white ketch-border-white;
          @apply md:hover:ketch-border-primary-text-color;

          &.is-trial-expired {
            @apply ketch-bg-white ketch-border-white;

            h4 {
              @apply ketch-text-primary-text-color;
              filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.15));
            }
          }
        }
      }
    }

    &.is-trial-expired {
      @apply ketch-bg-[#FF3459];

      .trial-status-banner {
        @apply ketch-flex-col ketch-space-y-c10 ketch-items-start sm:ketch-flex-row sm:ketch-space-y-0 sm:ketch-items-center;
      }
    }
  }
</style>
